import type { QueryClient } from '@tanstack/react-query'
import { redirect } from '@tanstack/react-router'

import { ensureRouteData } from '@/app/query-client'
import { pvRangeQueryOptions } from '@/features/brokers/api/queries'
import { defaultBrokerStock } from '@/features/brokers/lib/defaultTargets'
import { readLastBrokerStock } from '@/features/brokers/lib/lastUsed'
import type { RouterContext } from '@/routes/layouts/root'
import { DEFAULT_PERIOD } from '@/screens/brokers/search'

/**
 * Whether `symbol` is valid for PV range specifically — `'not-found'` is the
 * only miss that is NOT valid; `'no-floorsheet'` (a real stock, just quiet
 * this period) counts as valid, same as a real profile. Also folds a genuine
 * READ failure (a 5xx, a device that drops offline mid-check) into "not
 * valid" — see `ByStockRedirect.loader.ts`'s `validateLastBrokerStock` for
 * why a broken CHECK must fall through to the default rather than becoming
 * this route's own error page.
 */
async function validateLastPVRangeStock(client: QueryClient, symbol: string): Promise<boolean> {
  try {
    const result = await ensureRouteData(
      client,
      pvRangeQueryOptions(symbol, { period: DEFAULT_PERIOD }),
    )
    return result !== 'not-found'
  } catch {
    return false
  }
}

/**
 * The `/brokers/pv-range` loader — the sidebar's "PV range" row with no
 * symbol in view.
 *
 * Reads the SAME "last broker-analysis stock" `ByStockRedirect.loader.ts`
 * reads and writes (the owner's ruling: By stock and PV range share one
 * axis), then validates it against PV range's OWN endpoint before trusting
 * it — a symbol can be a perfectly good stock and still answer `'not-found'`
 * here if By stock never actually confirmed it. See `ByStockRedirect.loader.ts`
 * for the "always throws", "one fallback, never a loop" and
 * hover-preload-does-not-navigate notes; all apply here unchanged.
 */
export async function loadPVRangeRedirect({
  context: { queryClient },
}: {
  context: RouterContext
}): Promise<never> {
  const lastUsed = readLastBrokerStock()
  if (lastUsed !== null && (await validateLastPVRangeStock(queryClient, lastUsed))) {
    // eslint-disable-next-line @typescript-eslint/only-throw-error
    throw redirect({
      to: '/brokers/pv-range/$symbol',
      params: { symbol: lastUsed },
      replace: true,
    })
  }

  const symbol = await defaultBrokerStock(queryClient)
  await ensureRouteData(queryClient, pvRangeQueryOptions(symbol, { period: DEFAULT_PERIOD }))
  // eslint-disable-next-line @typescript-eslint/only-throw-error
  throw redirect({ to: '/brokers/pv-range/$symbol', params: { symbol }, replace: true })
}
